import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Target, Heart, Zap } from 'lucide-react';
import './AboutPage.css';

const AboutPage = () => {
  const observerRef = useRef(null);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = document.querySelectorAll('.animate-on-scroll');
    elements.forEach((el) => observerRef.current?.observe(el));

    return () => observerRef.current?.disconnect();
  }, []);

  return (
    <div className="about-page">
      <section className="about-hero">
        <div className="section-container">
          <div className="about-hero-content animate-on-scroll">
            <h1 className="about-title">
              Nous transformons le <span className="text-gradient">chaos opérationnel</span> en avantage compétitif
            </h1>
            <p className="about-subtitle">
              SYNA accompagne les PME et indépendants en croissance à structurer leur activité 
              pour gagner en performance, en sérénité et en rentabilité.
            </p>
          </div>
        </div>
      </section>

      <section className="section-padding">
        <div className="section-container">
          <div className="story-grid">
            <div className="story-image animate-on-scroll">
              <img 
                src="https://images.pexels.com/photos/7693713/pexels-photo-7693713.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Team Success"
              />
            </div>
            <div className="story-content animate-on-scroll">
              <h2 className="section-title">Notre histoire</h2>
              <p className="story-text">
                SYNA est né d'un constat simple : trop d'entreprises talentueuses plafonnent 
                à cause du chaos opérationnel. Des demandes clients qui se perdent, des équipes 
                qui ne savent plus qui fait quoi, des factures qui traînent...
              </p>
              <p className="story-text">
                Nous avons créé une méthode éprouvée pour structurer chaque maillon de la chaîne : 
                du premier contact client jusqu'à l'encaissement. Résultat ? Nos clients gagnent 
                en moyenne 40% de productivité et triplent leur ROI en 12 mois.
              </p>
              <p className="story-text">
                Notre mission : faire de la structuration opérationnelle un levier de croissance 
                accessible à toutes les entreprises en développement.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding values-section">
        <div className="section-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-title">Nos valeurs</h2>
            <p className="section-description">Ce qui guide chacune de nos interventions</p>
          </div>
          <div className="values-grid">
            <div className="value-card animate-on-scroll">
              <div className="value-icon">
                <Target size={32} />
              </div>
              <h3>Résultats concrets</h3>
              <p>
                Pas de blabla. On mesure l'impact de chaque action et on ajuste en temps réel. 
                Votre ROI est notre obsession.
              </p>
            </div>
            <div className="value-card animate-on-scroll">
              <div className="value-icon">
                <Heart size={32} />
              </div>
              <h3>Accompagnement humain</h3>
              <p>
                Votre équipe est au cœur de la transformation. On forme, on explique, on implique. 
                Pas de solution parachutée.
              </p>
            </div>
            <div className="value-card animate-on-scroll">
              <div className="value-icon">
                <Zap size={32} />
              </div>
              <h3>Pragmatisme</h3>
              <p>
                On ne vend pas la lune. On identifie vos vrais points de friction et on met 
                en place des solutions simples et efficaces.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="section-padding process-section">
        <div className="section-container">
          <div className="section-header animate-on-scroll">
            <h2 className="section-title">Notre méthode</h2>
            <p className="section-description">
              Un processus éprouvé en 4 étapes pour structurer votre croissance
            </p>
          </div>
          <div className="process-timeline">
            <div className="process-step animate-on-scroll">
              <div className="process-number">01</div>
              <div className="process-content">
                <h3>Diagnostic</h3>
                <p>
                  Audit complet de votre organisation : on identifie les points de friction, 
                  les pertes de temps et les opportunités d'amélioration.
                </p>
              </div>
            </div>
            <div className="process-step animate-on-scroll">
              <div className="process-number">02</div>
              <div className="process-content">
                <h3>Plan d'action</h3>
                <p>
                  On co-construit avec vous un plan de structuration sur-mesure, priorisé 
                  selon l'impact et la faisabilité.
                </p>
              </div>
            </div>
            <div className="process-step animate-on-scroll">
              <div className="process-number">03</div>
              <div className="process-content">
                <h3>Mise en œuvre</h3>
                <p>
                  On déploie les nouveaux processus et outils, on forme vos équipes, 
                  on ajuste en temps réel selon les retours terrain.
                </p>
              </div>
            </div>
            <div className="process-step animate-on-scroll">
              <div className="process-number">04</div>
              <div className="process-content">
                <h3>Suivi & autonomie</h3>
                <p>
                  On mesure les résultats, on ajuste si besoin, et on s'assure que votre 
                  équipe est 100% autonome pour la suite.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="about-cta">
        <div className="section-container">
          <div className="cta-content animate-on-scroll">
            <h2 className="cta-title">Prêt à structurer votre entreprise ?</h2>
            <p className="cta-text">
              Discutons de vos enjeux lors d'un diagnostic gratuit de 30 minutes
            </p>
            <Link to="/contact" className="btn btn-primary btn-large">
              Réserver mon diagnostic <ArrowRight size={20} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;